import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  AppointmentService,
  CreateContactRequest,
} from '../../../features/contact/services/appointment.service';
import { ToastService } from '../../services/toast.service';
import {
  DEFAULT_COUNTRY,
  getCountryByCode,
} from '../../../features/contact/utils/countries.util';
import {
  PhoneCountryInputComponent,
  PhoneCountryValue,
  phoneCountryValidator,
} from '../phone-country-input/phone-country-input.component';

@Component({
  selector: 'app-quick-contact-form',
  imports: [CommonModule, ReactiveFormsModule, PhoneCountryInputComponent],
  templateUrl: './quick-contact-form.component.html',
  styleUrl: './quick-contact-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.theme-dark]': "theme() === 'dark'",
    '[class.theme-light]': "theme() === 'light'",
  },
})
export class QuickContactFormComponent {
  /** Ajusta as cores para fundo claro (modal) ou escuro (hero/footer). */
  readonly theme = input<'light' | 'dark'>('light');
  /** Opcional: vincula o lead a um apartamento. */
  readonly apartmentId = input<number>();
  /** Emitido após o envio bem-sucedido (ex.: para fechar a modal). */
  readonly submitted = output<void>();

  private readonly appointmentService = inject(AppointmentService);
  private readonly toast = inject(ToastService);

  readonly isLoading = signal(false);

  readonly form = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.maxLength(255)]),
    email: new FormControl('', [Validators.required, Validators.email]),
    phone: new FormControl<PhoneCountryValue>(
      { countryCode: DEFAULT_COUNTRY.code, phoneNumber: '' },
      { nonNullable: true, validators: [phoneCountryValidator] },
    ),
    message: new FormControl('', [Validators.maxLength(400)]),
  });

  onSubmit(): void {
    if (this.isLoading()) {
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const phoneDigits = (value.phone.phoneNumber || '').replace(/\D/g, '');
    const country = getCountryByCode(value.phone.countryCode || DEFAULT_COUNTRY.code);
    const dialCode = (country?.dialCode || '+55').replace('+', '');

    const request: CreateContactRequest = {
      name: value.name!,
      email: value.email!,
      phoneNumber: dialCode + phoneDigits,
      services: [],
      reserved: false,
      skipScheduling: true,
    };

    const message = value.message?.trim();
    if (message) {
      request.message = message;
    }

    const apartmentId = this.apartmentId();
    if (apartmentId) {
      request.apartmentId = apartmentId;
    }

    this.isLoading.set(true);

    this.appointmentService.createContact(request).subscribe({
      next: () => {
        this.isLoading.set(false);
        this.toast.success(
          'Contato enviado!',
          'Em breve nossa equipe entrará em contato com você.',
        );
        this.resetForm();
        this.submitted.emit();
      },
      error: (error) => {
        this.isLoading.set(false);
        this.toast.error(
          'Não foi possível enviar',
          error?.error?.result?.message ||
            'Tente novamente em alguns instantes.',
        );
      },
    });
  }

  private resetForm(): void {
    this.form.reset({
      phone: { countryCode: DEFAULT_COUNTRY.code, phoneNumber: '' },
    });
  }
}
